import React from 'react';
import { useTranslation } from 'react-i18next';

import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { Box, Modal } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';

import { useAddRealtime } from '../features/Realtime';
import { useAddRecord } from '../features/pending';
import './Add.css';

const desktopModalSx = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '40rem',
    bgcolor: '#FFFFFF',
    borderRadius: '1rem',
    boxShadow: '0px 4px 25px 0px #EAEE9E9',
    p: 0,
};

function Add(props: any) {
    const { openAdd, setOpenAdd, setSaveAlert, setAlertState, tKey, handleAddUpdate, realtime } = props;
    const { t } = useTranslation(undefined, { keyPrefix: tKey });
    const addRealtime = useAddRealtime();
    const addRecord = useAddRecord();

    const [view, setView] = React.useState('add');

    const [deviceIds, setDeviceIds] = React.useState([{ id: 1, value: '' }]);
    const isMobile = useMediaQuery('(max-width: 768px)');

    const mobileModalSx = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '92vw',
        maxWidth: '360px',
        bgcolor: '#FFFFFF',
        borderRadius: '24px',
        boxShadow: '0px 12px 40px rgba(15, 23, 42, 0.25)',
        p: 0,
        maxHeight: '90vh',
        overflow: 'hidden',
    };

    const modalSx = isMobile ? mobileModalSx : desktopModalSx;

    const handleInputChange = (index: any, event: any) => {
        // convert lowercase to uppercase
        // max length 8
        event.target.value = event.target.value.toUpperCase();
        if (event.target.value.length > 8) {
            event.target.value = event.target.value.slice(0, 8);
        }
        const newDeviceIds = [...deviceIds];
        newDeviceIds[index].value = event.target.value;
        setDeviceIds(newDeviceIds);
    };

    const handleAddItem = () => {
        setDeviceIds([...deviceIds, { id: deviceIds.length + 1, value: '' }]);
    };

    const handleRemoveItem = () => {
        if (deviceIds.length > 1) {
            setDeviceIds(deviceIds.slice(0, -1));
        }
    };

    const handleAdd = (event: any) => {
        const target = event.target.id;
        if (target === 'add') {
            // setDeviceId(deviceIdInput.current.value);
            setView('confirm');
        }
        if (target === 'confirm') {
            setView('confirm');
        }
    };

    const handleConfirmCancel = () => {
        setView('add');
    };

    const handleConfirm = () => {
        const device_ids = deviceIds.map((deviceId: any) => deviceId.value);
        const onSuccess = () => {
            handleAddUpdate?.();
            setSaveAlert(true);
            setAlertState({ severity: 'success', message: t('addSuccess') });
        };
        const onError = () => {
            setSaveAlert(true);
            setAlertState({ severity: 'error', message: t('addFail') });
        };
        if (realtime) {
            // Refetches the Realtime grid itself on success.
            addRealtime.mutate(device_ids, { onSuccess, onError });
        } else {
            addRecord.mutate(device_ids, { onSuccess, onError });
        }

        setOpenAdd(false);
        setView('add');
    };

    const closeModal = () => {
        setView('add');
        setOpenAdd(false);
    };

    const renderInputs = (useMobileLayout: any = false) => {
        const wrapperClass = useMobileLayout ? 'addModal__field' : 'modal__form__item';
        const labelClass = useMobileLayout ? 'addModal__label' : 'modal__form__item__label';
        const inputClass = useMobileLayout ? 'addModal__input' : 'modal__form__item__input';

        return deviceIds.map((deviceId: any, index: any) => (
            <div key={deviceId.id} className={wrapperClass}>
                <label className={labelClass}>
                    {t('deviceId')} - {deviceId.id}
                </label>
                {useMobileLayout ? null : <div className='vline'></div>}
                <input
                    name={`deviceId${deviceId.id}`}
                    className={inputClass}
                    type='text'
                    placeholder='XXXXXXXX'
                    value={deviceId.value}
                    onChange={(event: any) => handleInputChange(index, event)}
                />
            </div>
        ));
    };

    const renderMobileAddView = () => (
        <>
            <div className='addModal__header'>
                <div className='addModal__title'>{t('addCotag')}</div>
                <button type='button' className='addModal__close' onClick={closeModal} aria-label='Close'>
                    &times;
                </button>
            </div>
            <div className='addModal__body'>
                {renderInputs(true)}
                <div className='addModal__iconRow'>
                    <button
                        type='button'
                        className='addModal__icon'
                        onClick={handleAddItem}
                        aria-label='Add device field'
                    >
                        <AddCircleOutlineIcon />
                    </button>
                    <button
                        type='button'
                        className='addModal__icon'
                        onClick={handleRemoveItem}
                        aria-label='Remove device field'
                    >
                        <RemoveCircleOutlineIcon />
                    </button>
                </div>
            </div>
            <div className='addModal__footer'>
                <button type='button' className='addModal__action addModal__action--ghost' onClick={closeModal}>
                    {t('cancel')}
                </button>
                <button
                    id='add'
                    type='button'
                    className='addModal__action addModal__action--primary'
                    onClick={handleAdd}
                >
                    {t('add')}
                </button>
            </div>
        </>
    );

    const renderMobileConfirmView = () => (
        <>
            <div className='addModal__header'>
                <div className='addModal__title'>{t('confirm')}</div>
                <button type='button' className='addModal__close' onClick={closeModal} aria-label='Close'>
                    &times;
                </button>
            </div>
            <div className='addModal__body addModal__body--confirm'>
                {deviceIds.map((deviceId: any) => (
                    <p key={deviceId.id} className='addModal__confirmText'>
                        {deviceId.value}
                    </p>
                ))}
                <p className='addModal__confirmText'>{t('confirmText')}</p>
            </div>
            <div className='addModal__footer'>
                <button
                    type='button'
                    className='addModal__action addModal__action--ghost'
                    onClick={handleConfirmCancel}
                >
                    {t('cancel')}
                </button>
                <button type='button' className='addModal__action addModal__action--primary' onClick={handleConfirm}>
                    {t('confirm')}
                </button>
            </div>
        </>
    );

    return (
        <Modal
            open={openAdd}
            onClose={closeModal}
            aria-labelledby='modal-modal-title'
            aria-describedby='modal-modal-description'
        >
            <Box sx={modalSx} className={`addModal ${isMobile ? 'addModal--mobile' : ''}`}>
                {isMobile ? (
                    view === 'add' ? (
                        renderMobileAddView()
                    ) : (
                        renderMobileConfirmView()
                    )
                ) : (
                    <>
                        <div className='button__container'>
                            <button
                                className='close'
                                onClick={() => {
                                    setView('add');
                                    setOpenAdd(false);
                                }}
                            >
                                &times;
                            </button>
                        </div>
                        <div style={{ display: view === 'add' ? 'inline' : 'none' }}>
                            <div className='modal__content'>
                                <form className='modal__form__container' id='addForm'>
                                    <div className='title'>{t('addCotag')}</div>
                                    {renderInputs()}
                                </form>
                            </div>
                            <div className='icon__container'>
                                <button className='icon' onClick={handleAddItem}>
                                    <AddCircleOutlineIcon />
                                </button>
                                <button className='icon' onClick={handleRemoveItem}>
                                    <RemoveCircleOutlineIcon />
                                </button>
                            </div>
                            <div className='button__container'>
                                <button id='add' className='save__all' onClick={handleAdd}>
                                    {t('add')}
                                </button>
                            </div>
                        </div>
                        <div style={{ display: view === 'confirm' ? 'inline' : 'none' }}>
                            <div className='modal__content'>
                                {deviceIds.map((deviceId: any) => (
                                    <p key={deviceId.id} className='confirmText'>
                                        {deviceId.value}
                                    </p>
                                ))}
                                <p className='confirmText'>{t('confirmText')}</p>
                                <button id='cancel' onClick={handleConfirmCancel}>
                                    {t('cancel')}
                                </button>
                                <button id='confirm' onClick={handleConfirm}>
                                    {t('confirm')}
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </Box>
        </Modal>
    );
}

export default Add;
